import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { InventoryItemDetail } from "../api/types";
import {
  INV_CATEGORIES,
  INV_DELIVERY_STATUSES,
  INV_STATUSES,
  INV_SUPPLIERS,
  invCategoryLabel,
  invDeliveryStatusLabel,
  invStatusLabel,
  isLowStock,
} from "../lib/inventory";

export function InventoryItemPage() {
  const { id } = useParams();
  const itemId = Number(id);

  const itemQ = useQuery({
    queryKey: ["inventory-item", itemId],
    queryFn: () => api.inventory.get(itemId),
    enabled: Number.isInteger(itemId),
  });

  if (itemQ.isLoading) {
    return (
      <Layout title="Inventory Item">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (itemQ.isError || !itemQ.data) {
    return (
      <Layout title="Inventory Item">
        <section className="card">
          <p>That item could not be found.</p>
          <Link to="/inventory" className="btn btn-ghost btn-xl">
            Back to Inventory
          </Link>
        </section>
      </Layout>
    );
  }

  return <InventoryItemView item={itemQ.data.item} />;
}

function InventoryItemView({ item }: { item: InventoryItemDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(item.name);
  const [category, setCategory] = useState(item.category ?? "");
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitOfMeasure, setUnitOfMeasure] = useState(item.unitOfMeasure ?? "");
  const [reorderThreshold, setReorderThreshold] = useState(
    item.reorderThreshold == null ? "" : String(item.reorderThreshold),
  );
  const [status, setStatus] = useState(item.status);
  const [deliveryStatus, setDeliveryStatus] = useState(item.deliveryStatus);
  const [supplier, setSupplier] = useState(item.supplier ?? "");
  const [notes, setNotes] = useState(item.notes ?? "");
  const [saved, setSaved] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      api.inventory.update(item.id, {
        name: name.trim(),
        category: category || null,
        quantity: quantity === "" ? 0 : Number(quantity),
        unitOfMeasure: unitOfMeasure.trim() || null,
        reorderThreshold: reorderThreshold === "" ? null : Number(reorderThreshold),
        status,
        deliveryStatus,
        supplier: supplier || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["inventory-item", item.id] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  const low = isLowStock(
    quantity === "" ? 0 : Number(quantity),
    reorderThreshold === "" ? null : Number(reorderThreshold),
  );

  return (
    <Layout title="Inventory Item">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>{item.name}</h2>
            <p className="sub">{item.buildingName}</p>
          </div>
          {low ? (
            <span className="badge badge-alert">Low — reorder</span>
          ) : (
            <span className="badge badge-ok">OK</span>
          )}
        </div>

        <p className="help-line">Edit the item and tap Save changes.</p>

        <label className="field">
          <span>Item name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Not set</option>
              {INV_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {invCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {INV_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {invStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Quantity on hand</span>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Unit of measure</span>
            <input value={unitOfMeasure} onChange={(e) => setUnitOfMeasure(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span>Reorder at (low-stock level)</span>
          <input
            type="number"
            min="0"
            step="any"
            value={reorderThreshold}
            onChange={(e) => setReorderThreshold(e.target.value)}
            placeholder="Leave blank for no alert"
          />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Ordered status</span>
            <select value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
              {INV_DELIVERY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {invDeliveryStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field">
            <span>Supplier</span>
            <div className="choice-set">
              {INV_SUPPLIERS.map((s) => (
                <label key={s} className={"choice" + (supplier === s ? " selected" : "")}>
                  <input
                    type="radio"
                    name="supplier"
                    checked={supplier === s}
                    onChange={() => setSupplier(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/inventory")}
            disabled={saveMut.isPending}
          >
            Back to list
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
